import type { overview as en } from '@/i18n/locales/en/overview';
import type { Translation } from '@/i18n/types';

export const overview: Translation<typeof en> = {
  page: {
    title: 'Επισκόπηση',
    lead: 'Πώς πάει η {{company}};',
    noScope: 'Επιλέξτε μια εταιρεία για να δείτε την επισκόπηση.',
    noDepartments: 'Κανένα τμήμα εδώ δεν έχει στατιστικά που μπορείτε να δείτε.',
    disclaimer:
      'Όλες οι τιμές είναι ενδεικτικές πληροφορίες ευεξίας σε επίπεδο ομάδας και δεν αποτελούν ιατρική διάγνωση.',
  },
  summary: {
    caption: 'Σύνοψη AI με βάση ανωνυμοποιημένα, συγκεντρωτικά δεδομένα.',
  },
  figures: {
    vitality: 'Ζωτικότητα',
    stress: 'Άγχος',
    sleep: 'Ύπνος',
    engagement: 'Συμμετοχή',
    activeParticipants: 'Ενεργοί συμμετέχοντες',
  },
  figuresInfo: {
    vitality:
      'Σύνθετη βαθμολογία με βάση τον ύπνο, τη δραστηριότητα, το HRV, το άγχος και τον καρδιακό ρυθμό.',
    stress: 'Μέσο επίπεδο άγχους, μετρημένο απευθείας από το wearable.',
    sleep: 'Μέση διάρκεια ύπνου ανά νύχτα, μετρημένη απευθείας από το wearable.',
    engagement: 'Ποσοστό εργαζομένων που χρησιμοποιούν ενεργά την εφαρμογή.',
    activeParticipants: 'Αριθμός εργαζομένων που ήταν ενεργοί την τελευταία περίοδο.',
  },
  legend: {
    title: 'Τι βλέπετε',
    measured:
      'Άμεσα μετρημένο — απευθείας από το wearable NOLATR ή έναν συνδεδεμένο αισθητήρα. Υψηλότερη αξιοπιστία.',
    aiDerived:
      'Προερχόμενο από AI — όχι άμεση μέτρηση. Η AI συνδυάζει πολλά σήματα σε έναν δείκτη.',
    usage: 'Χρήση & συμπεριφορά — πραγματικές αλληλεπιδράσεις στην εφαρμογή, όχι μέτρηση υγείας.',
  },
  developments: {
    title: 'Βασικές εξελίξεις',
    empty: 'Καμία αξιοσημείωτη εξέλιξη αυτή την περίοδο.',
    stress: {
      title: 'Ευκαιρία για ξεκούραση (αυξημένο άγχος)',
      body: 'Μια φιλική στιγμή χαλάρωσης ή μια συμβουλή αναπνοής μπορεί να βοηθήσει την ομάδα να ανανεωθεί.',
    },
    sleep: {
      title: 'Ευκαιρία: ύπνος κάτω από τον στόχο',
      body: 'Μια στοχευμένη, θετική συμβουλή αποκατάστασης μπορεί να στηρίξει καλά την ομάδα.',
    },
    vitalityGood: {
      title: 'Η ζωτικότητα εξελίσσεται καλά',
      body: 'Η ομάδα διατηρεί καλά τον τρέχοντα ρυθμό της — μια καλή στιγμή για να το αναγνωρίσετε.',
    },
    needsReactivation: {
      title: '{{count}} άτομα χρειάζονται επανενεργοποίηση',
      body: 'Ένα σύντομο, προσωπικό μήνυμα επανενεργοποίησης συνήθως λειτουργεί καλύτερα.',
    },
    mostActiveWeekday: {
      title: 'Περισσότερη ενέργεια: {{weekday}}',
      body: 'Προγραμματίστε προκλήσεις και επικοινωνία γύρω από την πιο ενεργή ημέρα για μέγιστη εμβέλεια.',
    },
    challengeParticipation: {
      title: 'Συμμετοχή σε προκλήσεις {{pct}}%',
      body: 'Μια νέα, εύκολα προσβάσιμη ομαδική πρόκληση θα μπορούσε να προσελκύσει περισσότερους ανθρώπους.',
    },
  },
  actions: {
    title: 'Ενέργειες για HR',
    empty: 'Καμία συνιστώμενη ενέργεια αυτή τη στιγμή.',
    sendReminder: {
      title: '{{count}} εργαζόμενοι δεν έχουν ενεργοποιηθεί ακόμη',
      subtitle: 'Στείλτε υπενθύμιση',
    },
    reactivate: {
      title: '{{count}} εργαζόμενοι χρειάζονται επανενεργοποίηση',
      subtitle: 'Επικοινωνήστε μαζί τους',
    },
    stressCheckIn: {
      title: 'Αυξημένο άγχος στην ομάδα',
      subtitle: 'Σκεφτείτε μια σύντομη συζήτηση για τον φόρτο εργασίας',
    },
    sleepReminder: {
      title: 'Ύπνος κάτω από τον στόχο στην ομάδα',
      subtitle: 'Σκεφτείτε μια υπενθύμιση για τις βραδινές συνήθειες και τον χρόνο οθόνης',
    },
    activeChallenge: {
      title: 'Η «{{name}}» βρίσκεται σε εξέλιξη — προγραμματίστε την επόμενη',
      subtitle: 'Προκλήσεις',
    },
    pilotEvaluation: {
      interim: {
        title: 'Το πιλοτικό «{{name}}» είναι έτοιμο για ενδιάμεση αξιολόγηση',
        subtitle: 'Δείτε το πιλοτικό',
      },
      final: {
        title: 'Το πιλοτικό «{{name}}» είναι έτοιμο για τελική αξιολόγηση',
        subtitle: 'Δείτε το πιλοτικό',
      },
    },
  },
  energy: {
    title: 'Ενέργεια & εργασιακή ευεξία',
    subtitle:
      'Πώς βιώνει η ομάδα την εργάσιμη ημέρα — ενέργεια, διαλείμματα, ρυθμός εργασίας και συμμετοχή. Μόνο συγκεντρωτικά, ποτέ ατομικά.',
    badge: 'Συγκεντρωτικά & ανωνυμοποιημένα',
    tbd: 'Προς καθορισμό',
    missingData: {
      noSource: 'Δείκτης ενέργειας και eNPS: η πηγή δεδομένων δεν έχει ακόμη καθοριστεί.',
      insufficientParticipants: 'Ανεπαρκείς συμμετέχοντες για {{metrics}}.',
    },
    privacyNote:
      'Υποστηρικτικές πληροφορίες, όχι παρακολούθηση. Ενδεικτική ευεξία σε επίπεδο ομάδας — όχι ιατρική ερμηνεία.',
    metrics: {
      energyIndex: 'Δείκτης ενέργειας',
      breakAdoption: 'Υιοθέτηση διαλειμμάτων',
      focusRhythm: 'Ρυθμός συγκέντρωσης',
      coachAdoption: 'Υιοθέτηση coach',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Κατάσταση πιλοτικών',
    empty: 'Δεν υπάρχουν ακόμη πιλοτικά.',
    participantsAndDay: '{{count}} συμμετέχοντες · ημέρα {{day}} από {{total}}',
    viewReport: 'Δείτε την αναφορά πιλότου',
  },
  challenges: {
    title: 'Ενεργές προκλήσεις',
    empty: 'Δεν υπάρχουν ακόμη προκλήσεις.',
    active: 'Ενεργές',
    total: 'Σύνολο',
    participants: 'Συμμετέχοντες',
    activeBadge: '{{count}} ενεργές',
    manageLink: 'Διαχείριση προκλήσεων',
    noneActive: 'Καμία ενεργή πρόκληση αυτή τη στιγμή.',
    participantsCount: '{{count}} συμμετέχοντες',
  },
  trends: {
    title: 'Τάσεις',
    filters: {
      allDepartments: 'Όλα τα τμήματα',
      participants: {
        label: 'Συμμετέχοντες',
        all: 'Όλοι οι συμμετέχοντες',
        manager: 'Διευθυντικά στελέχη',
        employee: 'Εργαζόμενοι',
      },
    },
    vitalityScore: 'Βαθμολογία ζωτικότητας με την πάροδο του χρόνου',
    activeUsersPerWeek: 'Ενεργοί χρήστες ανά εβδομάδα',
    sleep: 'Τάση ύπνου',
    stress: 'Τάση άγχους',
    hrv: 'Τάση HRV',
    recovery: 'Τάση ανάκαμψης',
    activity: 'Τάση δραστηριότητας',
    notAvailable:
      'Δεν υπάρχει ακόμη πηγή δεδομένων για αυτή την τάση — μη διαθέσιμο σε αυτή την έκδοση.',
    vsPreviousPeriod: 'σε σχέση με την προηγούμενη περίοδο',
    info: {
      vitalityScore: 'Σύνθετη βαθμολογία ζωτικότητας ανά ημέρα, με βάση όλα τα σήματα ζωτικότητας.',
      activeUsersPerWeek:
        'Αριθμός μοναδικών εργαζομένων που χρησιμοποίησαν ενεργά την εφαρμογή κάθε εβδομάδα.',
      sleep: 'Μέση διάρκεια ύπνου ανά ημέρα, μετρημένη απευθείας από το wearable.',
      stress: 'Μέσο επίπεδο άγχους ανά ημέρα, μετρημένο απευθείας από το wearable.',
      hrv: 'Μέση μεταβλητότητα καρδιακού ρυθμού (HRV) ανά ημέρα, δείκτης ανάκαμψης.',
      recovery:
        'Σύνθετη βαθμολογία ανάκαμψης ανά ημέρα, με βάση τον ύπνο, το HRV και τη δραστηριότητα.',
      activity: 'Μέσος αριθμός βημάτων ανά ημέρα, μετρημένος απευθείας από το wearable.',
    },
  },
};
